import { useState, type ReactNode } from "react"
import { ChevronDown, ChevronUp, Download, Lock, Rocket } from "lucide-react"
import { cn } from "@/lib/utils"

export interface DesktopDownloadBannerProps {
  title: string
  description?: ReactNode
  eyebrow?: string
  downloadLabel: string
  platformCaption?: string
  collapsed?: boolean
  defaultCollapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
  onDownload?: () => void
  /** When true, the action button becomes an upgrade CTA instead of a download. */
  upgradeMode?: boolean
  upgradeLabel?: string
  onUpgrade?: () => void
  expandAriaLabel?: string
  collapseAriaLabel?: string
  className?: string
}

export function DesktopDownloadBanner({
  title,
  description,
  eyebrow = "Required",
  downloadLabel,
  platformCaption,
  collapsed,
  defaultCollapsed = false,
  onCollapsedChange,
  onDownload,
  upgradeMode = false,
  upgradeLabel = "Upgrade",
  onUpgrade,
  expandAriaLabel = "Expand banner",
  collapseAriaLabel = "Collapse banner",
  className,
}: DesktopDownloadBannerProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed)
  const isControlled = collapsed !== undefined
  const isCollapsed = isControlled ? collapsed : internalCollapsed

  const setCollapsed = (next: boolean) => {
    if (!isControlled) setInternalCollapsed(next)
    onCollapsedChange?.(next)
  }

  if (isCollapsed) {
    return (
      <section
        className={cn(
          "rounded-xl border border-transparent shadow-sm [background-image:linear-gradient(var(--color-card),var(--color-card)),linear-gradient(to_right,var(--color-amber-400),var(--color-amber-500),var(--color-amber-600))] [background-origin:border-box] [background-clip:padding-box,border-box]",
          className,
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label={expandAriaLabel}
          className="relative w-full rounded-[11px] px-4 sm:px-5 py-2.5 flex items-center gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer"
        >
          {upgradeMode ? (
            <Lock size={14} strokeWidth={2} className="text-amber-500 shrink-0" />
          ) : (
            <Rocket size={14} strokeWidth={2} className="text-amber-500 shrink-0" />
          )}
          <span className="text-label font-semibold text-foreground truncate flex-1 tracking-tight">
            {title}
          </span>
          {eyebrow && (
            <span className="hidden sm:inline text-caption uppercase tracking-wider text-amber-600 dark:text-amber-400 shrink-0">
              {eyebrow}
            </span>
          )}
          <ChevronDown
            size={14}
            strokeWidth={2.25}
            className="text-muted-foreground shrink-0"
          />
        </button>
      </section>
    )
  }

  return (
    <section
      className={cn(
        "relative rounded-xl overflow-hidden bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 shadow-lg shadow-amber-500/30 ring-1 ring-inset ring-amber-300/60",
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"
      />

      <button
        type="button"
        onClick={() => setCollapsed(true)}
        aria-label={collapseAriaLabel}
        className="absolute top-2 right-2 z-20 p-1 rounded text-zinc-950/70 hover:text-zinc-950 hover:bg-zinc-950/10 transition-colors cursor-pointer"
      >
        <ChevronUp size={14} strokeWidth={2.25} />
      </button>

      <div className="relative px-4 sm:px-5 py-4 pr-4 sm:pr-10 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="sm:hidden flex flex-col gap-2">
          {eyebrow && <span className="text-caption text-zinc-950/85">{eyebrow}</span>}
          <div className="flex flex-col gap-1">
            <h3 className="text-page-title text-zinc-950">{title}</h3>
            {description && <p className="text-body text-zinc-950/80">{description}</p>}
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-4 flex-1 min-w-0">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-zinc-950/15 border border-zinc-950/15 ring-1 ring-inset ring-white/15 flex items-center justify-center">
            {upgradeMode ? (
              <Lock size={18} strokeWidth={1.75} className="text-zinc-950" />
            ) : (
              <Rocket size={18} strokeWidth={1.75} className="text-zinc-950" />
            )}
          </div>

          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              {eyebrow && (
                <>
                  <span className="text-caption text-zinc-950/85 shrink-0 leading-none translate-y-[1px]">
                    {eyebrow}
                  </span>
                  <span
                    aria-hidden="true"
                    className="w-1 h-1 rounded-full bg-zinc-950/60 shrink-0 translate-y-[1px]"
                  />
                </>
              )}
              <h3 className="text-page-title text-zinc-950 truncate leading-tight">{title}</h3>
            </div>
            {description && (
              <p className="text-body text-zinc-950/80 max-w-xl">{description}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-stretch sm:items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={upgradeMode ? onUpgrade : onDownload}
            className="inline-flex items-center justify-center gap-2 h-11 sm:h-10 px-4 rounded-lg text-label text-amber-50 bg-zinc-950 hover:bg-zinc-900 border border-zinc-950 shadow-md shadow-zinc-950/30 transition-colors cursor-pointer w-full sm:w-auto"
          >
            {!upgradeMode && <Download size={14} strokeWidth={2.25} />}
            {upgradeMode ? upgradeLabel : downloadLabel}
          </button>
          {!upgradeMode && platformCaption && (
            <span className="text-caption text-zinc-950/75 text-center">
              {platformCaption}
            </span>
          )}
        </div>
      </div>
    </section>
  )
}
